import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import { CreateEncounterContext } from "@/contexts/create-encounter";
import {
  EncounterChargeItemRowsResult,
  getEncounterChargeItemRows,
} from "@/encounters/services/encounter-charge-item-rows";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

type UseEncounterChargeItemRowsParams = {
  clientId: string | undefined;
  encounterId: string | undefined;
};

type UseEncounterChargeItemRows = EncounterChargeItemRowsResult & {
  isLoading: boolean;
  /** True when the read failed, so an outage is not shown as an encounter with nothing charged. */
  hasLoadFailed: boolean;
  /**
   * Reads the charges again: the retry offered when the read failed, and what a save calls once it
   * has written staged entries the table now has to show as real charges.
   */
  reload: () => void;
};

/**
 * Loads the charges an encounter has captured, for the biller section to render.
 *
 * Read-only: what the biller stages and what saving it writes live in
 * useStagedBillerSectionProcedures, and this hook is told to read again once that has landed.
 * A failed read is reported separately from an empty result —
 * "this encounter has not been charged for anything" and "we could not find out" are different
 * things to tell a biller, and only one of them is a reason to retry.
 *
 * Signing and locking captures the charges, and it happens on this page: a biller looking at an
 * encounter still being documented sees the empty state, and the charges appear underneath them.
 * `statusChanged` is what the encounter's own status cannot be — the encounter resource the section
 * is handed is not refreshed in place once it is signed, while this flag is toggled by the signing
 * navigation and so is the app's signal that an encounter's status moved.
 */
function useEncounterChargeItemRows({
  clientId,
  encounterId,
}: UseEncounterChargeItemRowsParams): UseEncounterChargeItemRows {
  const medplum = useMedplum();
  const { statusChanged } = useContext(CreateEncounterContext);
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(true);
  const [chargeItemRows, setChargeItemRows] = useState<EncounterChargeItemRowsResult>({
    rows: [],
    placeOfService: undefined,
  });
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  // Only the newest read is allowed to report anything. Signing, a retry or a change of encounter
  // can each start one while an earlier one is still in flight, and the slower of the two must not
  // replace what the newer one found.
  const latestLoadIdRef = useRef(0);
  // The encounter `chargeItemRows` belongs to. The encounter page is not remounted on an
  // `:encounterId` route change (no ancestor keys it by encounter), so a route change only
  // re-renders this hook with a new `encounterId` prop — the reload effect below still needs to
  // commit before it can clear `chargeItemRows`. Comparing against this on every render, instead
  // of only resetting from inside that effect, masks the previous encounter's rows and Location on
  // the very first render after the change too, not just once the effect has run.
  const [loadedEncounterKey, setLoadedEncounterKey] = useState<string | undefined>(undefined);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  const currentEncounterKey = clientId && encounterId ? `${clientId}/${encounterId}` : undefined;
  const isDifferentEncounter = currentEncounterKey !== loadedEncounterKey;

  useEffect(() => {
    const loadId = ++latestLoadIdRef.current;
    const isLatestLoad = () => loadId === latestLoadIdRef.current;

    const load = async () => {
      startLoading();

      // Nothing says which encounter to read, so the section has no charges to show and no way to
      // find out — the same thing a failed read leaves it with, and told the same way. Both ids come
      // from the encounter's own route, so this is a broken page rather than a state a biller
      // reaches; an empty table here would read as an encounter that was not charged for anything.
      if (!clientId || !encounterId) {
        setChargeItemRows({ rows: [], placeOfService: undefined });
        setLoadedEncounterKey(undefined);
        setHasLoadFailed(true);
        stopLoading();
        return;
      }

      try {
        const loaded = await getEncounterChargeItemRows({ medplum, clientId, encounterId });
        if (!isLatestLoad()) return;

        setChargeItemRows(loaded);
        setLoadedEncounterKey(`${clientId}/${encounterId}`);
        setHasLoadFailed(false);
      } catch (error) {
        if (!isLatestLoad()) return;

        // Only the encounter's id reaches Sentry: what was charged for is clinical, and a failure to
        // display it is an operational problem.
        captureExceptionWithoutPhi("Failed to read the encounter's captured charge items", error, {
          encounterId,
        });
        setChargeItemRows({ rows: [], placeOfService: undefined });
        setLoadedEncounterKey(`${clientId}/${encounterId}`);
        setHasLoadFailed(true);
      } finally {
        if (isLatestLoad()) stopLoading();
      }
    };

    void load();
  }, [clientId, encounterId, medplum, statusChanged, reloadCount, startLoading, stopLoading]);

  if (isDifferentEncounter) {
    return { isLoading: true, rows: [], placeOfService: undefined, hasLoadFailed: false, reload };
  }

  return { isLoading, ...chargeItemRows, hasLoadFailed, reload };
}

export default useEncounterChargeItemRows;
